import axios from "axios";

interface PushSubscriptionData {
  endpoint: string;
  key: string | null;
  token: string | null;
  encoding: string;
}

export default function useWebPush() {
  const isAvailable = (): boolean => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
    if (!isAvailable()) return null;

    try {
      let registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        registration = await navigator.serviceWorker.register("/sw.js");
      }

      return registration;
    } catch (error) {
      console.error("Error getting service worker registration:", error);
      return null;
    }
  };

  const getPushSubscription = async (): Promise<PushSubscription | null> => {
    const registration = await getServiceWorkerRegistration();

    return (await registration?.pushManager.getSubscription()) ?? null;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer))));

  const subscribe = async (subscription: PushSubscription): Promise<void> => {
    try {
      const key = subscription.getKey("p256dh");
      const token = subscription.getKey("auth");
      const contentEncoding = (PushManager.supportedContentEncodings || ["aesgcm"])[0];

      const data: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        key: key ? arrayBufferToBase64(key) : null,
        token: token ? arrayBufferToBase64(token) : null,
        encoding: contentEncoding,
      };

      await axios.post(window.route("ajax.notifications.web-push.subscribe"), data);
    } catch (e) {
      console.error(e);
      alert("푸시 등록에 실패 했습니다. 채팅으로 고객센터에 문의해 주세요");
    }
  };

  const unsubscribe = async (): Promise<void> => {
    try {
      const subscription = await getPushSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(window.route("ajax.notifications.web-push.unsubscribe"), {
          data: { endpoint: subscription.endpoint },
        });
      }
    } catch (e) {
      console.error(e);
      alert("푸시 해제에 실패 했습니다. 채팅으로 고객센터에 문의해 주세요");
    }
  };

  const enablePushNotification = async (): Promise<void> => {
    if (!isAvailable()) {
      throw new Error("푸시 알림을 사용할 수 없는 환경입니다. 자세한 사항은 고객센터에 문의해 주세요");
    }

    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      throw new Error("푸시를 사용하기 위해서는 브라우저 알림 권한을 허용해 주세요.");
    }

    if (!window.navigator || !("serviceWorker" in navigator)) {
      throw new Error("푸시 알림을 사용할 수 없는 환경입니다. 자세한 사항은 고객센터에 문의해 주세요");
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      return;
    }

    const newSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: getVAPIDPublicKey(),
    });

    return subscribe(newSubscription);
  };

  const disablePushNotification = async (): Promise<void> => {
    await unsubscribe();
  };

  const getVAPIDPublicKey = (): string => {
    const key = import.meta.env.VITE_VAPID_PUBLIC_KEY;

    if (!key) throw new Error("VAPID public key is not set");

    return key;
  };

  return {
    isAvailable,
    getServiceWorkerRegistration,
    getPushSubscription,
    subscribe,
    unsubscribe,
    enablePushNotification,
    disablePushNotification,
    getVAPIDPublicKey,
  };
}
